import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import CloseIcon from '../Icons/CloseIcon'
import WarningIcon from '../../assets/icons/warning-with-circle.png'
import './VariantBottomSheet.css'

const DEFAULT_SUBTITLE =
  'Your MySaathi care plan depends on the variant you have been prescribed.'

const DEFAULT_NOTE =
  'Your invoice must be for the variant you select here. If the invoice does not match your selection, your request will be <b>rejected</b> and you will not receive any MySaathi care program benefits.'

const DEFAULT_HINT = 'Select a variant to continue'

const formatStrength = (variantId) => {
  if (!variantId) return ''
  return String(variantId).replace(/(\d+)\s*(mg)/i, '$1 $2')
}

const VariantBottomSheet = ({
  show = false,
  config,
  selectedVariant,
  onSelect,
  onClose,
  onProceed,
  proceedDisabled = false,
}) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [show])

  const noteHtml = useMemo(() => {
    if (selectedVariant && config?.invoiceWarning) {
      return config.invoiceWarning.replace(
        /\{\{\s*strength\s*\}\}/g,
        formatStrength(selectedVariant),
      )
    }
    return config?.note || DEFAULT_NOTE
  }, [config?.invoiceWarning, config?.note, selectedVariant])

  if (!show) return null

  const title = config?.title || 'Which MySaathi variant are you on?'
  const subtitle = config?.subTitle || DEFAULT_SUBTITLE
  const hint = config?.hint || DEFAULT_HINT
  const options = Array.isArray(config?.options) ? config.options : []

  const handleClose = () => {
    onClose?.()
    document.body.style.overflow = 'auto'
  }

  return createPortal(
    <>
      <div className="msv-overlay" onClick={handleClose} aria-hidden="true" />
      <div
        className="msv-sheet msv-open"
        id="msv-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="msv-title"
        aria-hidden="false"
      >
        <div className="msv-grabber" />

        <div className="msv-head">
          <button
            type="button"
            className="msv-close"
            id="msv-close"
            aria-label="Close"
            onClick={handleClose}
          >
            <CloseIcon color="currentColor" width={16} height={16} />
          </button>
          <p className="msv-title" id="msv-title">
            {title}
          </p>
          <p className="msv-sub">{subtitle}</p>
        </div>

        <div className="msv-body" role="radiogroup" aria-labelledby="msv-title">
          {options.map((option, index) => {
            const selected = selectedVariant === option?.id
            const kitItems = Array.isArray(option?.kit) ? option.kit : []

            return (
              <div
                key={option?.id}
                className="msv-option"
                id={`msv-opt-${option?.id}`}
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                onClick={() => onSelect?.(option?.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect?.(option?.id)
                  }
                }}
              >
                <div className="msv-option-top">
                  <div className="msv-radio" />
                  <div style={{ flex: 1 }}>
                    <p className="msv-name">{option?.title}</p>
                    {option?.meta ? (
                      <span
                        className={`msv-pill${
                          option?.pillVariant === 'lite' || index > 0
                            ? ' msv-pill--lite'
                            : ''
                        }`}
                      >
                        {option.meta}
                      </span>
                    ) : null}
                    {kitItems.length ? (
                      <div className="msv-kit">
                        <p className="msv-kit-h">Welcome kit</p>
                        <p className="msv-kit-b">{kitItems.join(' · ')}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}

          <div className="msv-note">
            <img
              src={WarningIcon}
              alt=""
              aria-hidden="true"
              width={26}
              height={26}
            />
            <p
              id="msv-note-text"
              dangerouslySetInnerHTML={{ __html: noteHtml }}
            />
          </div>
        </div>

        <div className="msv-foot">
          <button
            type="button"
            className="msv-cta"
            id="msv-proceed"
            disabled={proceedDisabled}
            onClick={onProceed}
          >
            Proceed
          </button>
          {proceedDisabled ? (
            <p className="msv-hint" id="msv-hint">
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    </>,
    document.body,
  )
}

export default VariantBottomSheet
